import { Building2, Clock, Layers, Plug, RefreshCw, Users } from "lucide-react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { useIntegrationAccounts, useRefreshAccounts } from "@/hooks/use-integration-accounts";
import { useIntegrationResources, useRefreshResources } from "@/hooks/use-integration-resources";
import {
  useImportSolutions,
  useIntegrationSolutions,
  useRefreshSolutions,
} from "@/hooks/use-integration-solutions";
import {
  useIntegrationTimeEntries,
  useRefreshTimeEntries,
} from "@/hooks/use-integration-time-entries";
import { useRefreshAllIntegrations } from "@/hooks/use-integrations";
import { type DirectoryItem, lastSyncedLabel, SyncObjectCard } from "./SyncObjectCard";

export function SyncPanel() {
  const solutions = useIntegrationSolutions();
  const accounts = useIntegrationAccounts();
  const resources = useIntegrationResources();
  const timeEntries = useIntegrationTimeEntries();

  const refreshAll = useRefreshAllIntegrations();
  const refreshSolutions = useRefreshSolutions();
  const refreshAccounts = useRefreshAccounts();
  const refreshResources = useRefreshResources();
  const refreshTimeEntries = useRefreshTimeEntries();
  const importSolutions = useImportSolutions();

  const isLoading =
    solutions.isLoading || accounts.isLoading || resources.isLoading || timeEntries.isLoading;
  const error = solutions.error ?? accounts.error ?? resources.error ?? timeEntries.error;

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (error) {
    return (
      <ErrorState
        message={error.message}
        onRetry={() => {
          solutions.refetch();
          accounts.refetch();
          resources.refetch();
          timeEntries.refetch();
        }}
      />
    );
  }
  if (!solutions.data || !accounts.data || !resources.data || !timeEntries.data) {
    return null;
  }

  // Every integration endpoint reports the same `configured` flag.
  const configured = solutions.data.configured && accounts.data.configured;

  const solutionLinked: DirectoryItem[] = solutions.data.linked.map((s) => ({
    key: s.nymblSolutionId,
    name: s.name,
    isActive: s.isActive,
    lastSyncedAt: s.lastSyncedAt,
  }));
  const solutionStaged: DirectoryItem[] = solutions.data.available.map((s) => ({
    key: s.nymblSolutionId,
    name: s.name,
    isActive: s.isActive,
    lastSyncedAt: s.lastSyncedAt,
  }));
  const accountLinked: DirectoryItem[] = accounts.data.linked.map((a) => ({
    key: a.nymblAccountId,
    name: a.name,
    isActive: a.isActive,
    lastSyncedAt: a.lastSyncedAt,
  }));
  const accountStaged: DirectoryItem[] = accounts.data.available.map((a) => ({
    key: a.nymblAccountId,
    name: a.name,
    isActive: a.isActive,
    lastSyncedAt: a.lastSyncedAt,
  }));
  const resourceItems: DirectoryItem[] = resources.data.linked.map((r) => ({
    key: r.nymblPersonId,
    name: r.name,
    isActive: r.isActive,
    lastSyncedAt: r.lastSyncedAt,
  }));
  const timeEntrySynced = timeEntries.data.lastSyncedAt
    ? new Date(timeEntries.data.lastSyncedAt).toLocaleString()
    : null;

  const anyRefreshing =
    refreshAll.isPending ||
    refreshSolutions.isPending ||
    refreshAccounts.isPending ||
    refreshResources.isPending ||
    refreshTimeEntries.isPending;

  return (
    <div className="space-y-4">
      <Card className="border-border/50">
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <Plug className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">Nymbl Platform</CardTitle>
              <p className="text-sm text-muted-foreground">
                {configured
                  ? "Pull the latest Solutions, Accounts, Resources and hours from the Nymbl gateway."
                  : "Integration not configured (NYMBL_API_BASE_URL / NYMBL_API_TOKEN unset)."}
              </p>
            </div>
          </div>
          <Button onClick={() => refreshAll.mutate()} disabled={!configured || anyRefreshing}>
            <RefreshCw
              className={refreshAll.isPending ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"}
            />
            {refreshAll.isPending ? "Refreshing…" : "Refresh all"}
          </Button>
        </CardHeader>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <SyncObjectCard
          title="Solutions"
          icon={<Layers className="h-5 w-5 text-primary" />}
          syncedLabel={lastSyncedLabel([...solutionLinked, ...solutionStaged])}
          tiles={[
            { label: "Imported", count: solutionLinked.length, items: solutionLinked },
            { label: "Discovered", count: solutionStaged.length, items: solutionStaged },
          ]}
          onRefresh={() => refreshSolutions.mutate()}
          refreshing={refreshSolutions.isPending}
          refreshDisabled={!configured || anyRefreshing}
          action={
            solutionStaged.length > 0 ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => importSolutions.mutate()}
                disabled={importSolutions.isPending}
              >
                {importSolutions.isPending ? "Importing…" : `Import ${solutionStaged.length}`}
              </Button>
            ) : null
          }
        />
        <SyncObjectCard
          title="Accounts"
          icon={<Building2 className="h-5 w-5 text-primary" />}
          syncedLabel={lastSyncedLabel([...accountLinked, ...accountStaged])}
          tiles={[
            { label: "Imported", count: accountLinked.length, items: accountLinked },
            { label: "Discovered", count: accountStaged.length, items: accountStaged },
          ]}
          onRefresh={() => refreshAccounts.mutate()}
          refreshing={refreshAccounts.isPending}
          refreshDisabled={!configured || anyRefreshing}
        />
        <SyncObjectCard
          title="Resources"
          icon={<Users className="h-5 w-5 text-primary" />}
          syncedLabel={lastSyncedLabel(resourceItems)}
          tiles={[{ label: "Synced from NOS", count: resourceItems.length, items: resourceItems }]}
          onRefresh={() => refreshResources.mutate()}
          refreshing={refreshResources.isPending}
          refreshDisabled={!configured || anyRefreshing}
        />
        <SyncObjectCard
          title="Time Entry"
          icon={<Clock className="h-5 w-5 text-primary" />}
          syncedLabel={timeEntrySynced}
          tiles={[{ label: "Weekly hours rows", count: timeEntries.data.weeksSynced }]}
          onRefresh={() => refreshTimeEntries.mutate()}
          refreshing={refreshTimeEntries.isPending}
          refreshDisabled={!configured || anyRefreshing}
        />
      </div>
    </div>
  );
}
